import React, { useMemo, useState } from 'react';
import { Input, Select, Table, Button, Space, message } from 'antd';
import {
  SearchOutlined,
  CheckCircleFilled,
  ClockCircleFilled,
  CloseCircleFilled,
  DownloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';

/** One nominated award, any status, as returned in the Insights payload's awardDetails. */
export interface AwardDetail {
  nomineeId: string;
  nomineeName: string;
  nomineeEmail: string | null;
  country: string;
  parentAwardType: string;
  awardType: string;
  status: 'Awarded' | 'Approved' | 'Pending' | 'Rejected' | string;
  amount: number;
  date: string;
  managerId: string | null;
  managerName: string;
  unitHeadId: string | null;
  unitHeadName: string;
}

type GroupBy = 'Unit head' | 'Country' | 'Award type' | 'Manager';

type Level = 'unitHead' | 'manager' | 'country' | 'parentAward' | 'award' | 'employee' | 'item';

interface Node {
  key: string;
  level: Level;
  name: string;
  sub?: string;
  status?: string;
  employees: number;
  awards: number;
  amount: number;
  children?: Node[];
}

// Same status steps as the Insights charts; each ships with an icon and label, never colour alone.
const STATUS: Record<string, { color: string; icon: React.ReactNode }> = {
  Awarded: { color: '#0ca30c', icon: <CheckCircleFilled /> },
  Approved: { color: '#2a78d6', icon: <CheckCircleFilled /> },
  Pending: { color: '#fab219', icon: <ClockCircleFilled /> },
  Rejected: { color: '#d03b3b', icon: <CloseCircleFilled /> },
};

// Status sorts by workflow stage rather than alphabetically.
const STATUS_ORDER: Record<string, number> = { Awarded: 0, Approved: 1, Pending: 2, Rejected: 3 };

const LEVEL_LABEL: Record<Level, string> = {
  unitHead: 'Unit head',
  manager: 'Manager',
  country: 'Country',
  parentAward: 'Award category',
  award: 'Award type',
  employee: 'Employee',
  item: 'Award',
};

const num = (v: number) => v.toLocaleString('en-US');
const usdExact = (v: number) =>
  `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const NOT_SPECIFIED = 'Not specified';

/** Levels below the chosen group-by, ending at the employee who received the award. */
const PATHS: Record<GroupBy, Array<{ level: Level; id: (r: AwardDetail) => string; name: (r: AwardDetail) => string }>> = {
  'Unit head': [
    { level: 'unitHead', id: (r) => r.unitHeadId || NOT_SPECIFIED, name: (r) => r.unitHeadName },
    { level: 'manager', id: (r) => r.managerId || NOT_SPECIFIED, name: (r) => r.managerName },
  ],
  Country: [{ level: 'country', id: (r) => r.country, name: (r) => r.country }],
  'Award type': [
    { level: 'parentAward', id: (r) => r.parentAwardType, name: (r) => r.parentAwardType },
    { level: 'award', id: (r) => `${r.parentAwardType}|${r.awardType}`, name: (r) => r.awardType },
  ],
  Manager: [{ level: 'manager', id: (r) => r.managerId || NOT_SPECIFIED, name: (r) => r.managerName }],
};

const byValue = (a: Node, b: Node) => {
  // "Not specified" buckets sink to the bottom so real people/categories lead.
  if ((a.name === NOT_SPECIFIED) !== (b.name === NOT_SPECIFIED)) return a.name === NOT_SPECIFIED ? 1 : -1;
  return b.amount - a.amount || b.awards - a.awards || a.name.localeCompare(b.name);
};

function buildTree(rows: AwardDetail[], groupBy: GroupBy): Node[] {
  const path = PATHS[groupBy];

  const build = (slice: AwardDetail[], depth: number, prefix: string): Node[] => {
    // Past the grouping levels: one node per employee, whose children are their awards.
    if (depth === path.length) {
      const byEmployee = new Map<string, AwardDetail[]>();
      slice.forEach((r) => byEmployee.set(r.nomineeId, [...(byEmployee.get(r.nomineeId) || []), r]));
      return Array.from(byEmployee.entries())
        .map(([id, items]) => {
          const key = `${prefix}/e:${id}`;
          return {
            key,
            level: 'employee' as Level,
            name: items[0].nomineeName,
            sub: items[0].nomineeEmail || undefined,
            employees: 1,
            awards: items.length,
            amount: items.reduce((s, r) => s + r.amount, 0),
            children: items
              .slice()
              .sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf())
              .map((r, i) => ({
                key: `${key}/a:${i}`,
                level: 'item' as Level,
                name: groupBy === 'Award type' ? r.awardType : `${r.parentAwardType} › ${r.awardType}`,
                sub: dayjs(r.date).format('DD MMM YYYY'),
                status: r.status,
                employees: 0,
                awards: 1,
                amount: r.amount,
              })),
          };
        })
        .sort(byValue);
    }

    const step = path[depth];
    const groups = new Map<string, AwardDetail[]>();
    slice.forEach((r) => {
      const id = step.id(r);
      groups.set(id, [...(groups.get(id) || []), r]);
    });

    return Array.from(groups.entries())
      .map(([id, items]) => {
        const key = `${prefix}/${step.level}:${id}`;
        return {
          key,
          level: step.level,
          name: step.name(items[0]) || NOT_SPECIFIED,
          employees: new Set(items.map((r) => r.nomineeId)).size,
          awards: items.length,
          amount: items.reduce((s, r) => s + r.amount, 0),
          children: build(items, depth + 1, key),
        };
      })
      .sort(byValue);
  };

  return build(rows, 0, groupBy);
}

const AwardsDrilldown: React.FC<{ rows: AwardDetail[] }> = ({ rows }) => {
  const [groupBy, setGroupBy] = useState<GroupBy>('Unit head');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<React.Key[]>([]);
  // Controlled paging: a fixed pageSize prop would pin the table to 10 rows and the
  // size changer would snap back on every pick.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.nomineeName, r.nomineeEmail, r.managerName, r.unitHeadName, r.country, r.awardType, r.parentAwardType]
        .some((v) => (v || '').toLowerCase().includes(q))
    );
  }, [rows, search]);

  const tree = useMemo(() => buildTree(filtered, groupBy), [filtered, groupBy]);

  const totals = useMemo(
    () => ({
      employees: new Set(filtered.map((r) => r.nomineeId)).size,
      awards: filtered.length,
      amount: filtered.reduce((s, r) => s + r.amount, 0),
    }),
    [filtered]
  );

  const changeGroupBy = (next: GroupBy) => {
    setGroupBy(next);
    setPage(1);
    setExpanded([]);
  };

  // Exports what the table holds (current filters + search), one row per award, with the
  // group-by columns first and rows ordered the way the tree is: by group, then employee.
  const downloadExcel = () => {
    if (!filtered.length) {
      message.warning('No award recipients to export.');
      return;
    }

    const groupColumns: Record<GroupBy, Array<[string, (r: AwardDetail) => string]>> = {
      'Unit head': [
        ['Unit Head', (r) => r.unitHeadName],
        ['Manager', (r) => r.managerName],
      ],
      Country: [['Country', (r) => r.country]],
      'Award type': [
        ['Award Category', (r) => r.parentAwardType],
        ['Award Type', (r) => r.awardType],
      ],
      Manager: [['Manager', (r) => r.managerName]],
    };
    const lead = groupColumns[groupBy];
    const leadNames = new Set(lead.map(([name]) => name));

    const rest: Array<[string, (r: AwardDetail) => string | number | Date]> = [
      ['Employee', (r) => r.nomineeName],
      ['Email', (r) => r.nomineeEmail || ''],
      ['Country', (r) => r.country],
      ['Manager', (r) => r.managerName],
      ['Unit Head', (r) => r.unitHeadName],
      ['Award Category', (r) => r.parentAwardType],
      ['Award Type', (r) => r.awardType],
      ['Status', (r) => r.status],
      ['Date', (r) => dayjs(r.date).toDate()],
      ['Value ($)', (r) => Math.round(r.amount * 100) / 100],
    ];
    const columns = [...lead, ...rest.filter(([name]) => !leadNames.has(name))];

    const sorted = filtered.slice().sort((a, b) => {
      for (const [, get] of lead) {
        const diff = get(a).localeCompare(get(b));
        if (diff) return diff;
      }
      return a.nomineeName.localeCompare(b.nomineeName) || dayjs(b.date).valueOf() - dayjs(a.date).valueOf();
    });

    const sheet = XLSX.utils.aoa_to_sheet(
      [columns.map(([name]) => name), ...sorted.map((r) => columns.map(([, get]) => get(r)))],
      { cellDates: true, dateNF: 'dd mmm yyyy' }
    );
    sheet['!cols'] = columns.map(([name]) => ({ wch: name === 'Email' ? 32 : name === 'Value ($)' ? 12 : 24 }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, 'Award recipients');
    XLSX.writeFile(
      workbook,
      `Award_Recipients_by_${groupBy.replace(' ', '_')}_${dayjs().format('YYYY-MM-DD')}.xlsx`
    );
  };

  return (
    <div className="ins-card">
      <div className="ins-card-head" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div className="ins-card-title">Award recipients</div>
        </div>
        <Space wrap>
          <Select
            value={groupBy}
            onChange={(v) => changeGroupBy(v as GroupBy)}
            style={{ width: 150 }}
            aria-label="Group by"
            options={(['Unit head', 'Country', 'Award type', 'Manager'] as GroupBy[]).map((g) => ({
              label: g,
              value: g,
            }))}
          />
          <Input
            className="ins-drill-search"
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search employee, manager, unit head"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            style={{ width: 260 }}
          />
          <Button className="ins-drill-btn" icon={<DownloadOutlined />} onClick={downloadExcel} disabled={!filtered.length}>
            Excel
          </Button>
        </Space>
      </div>
      <div className="ins-card-body ins-table-scroll">
        <div style={{ fontSize: 12, color: '#52514e', marginBottom: 8 }}>
          {num(tree.length)} {groupBy === 'Award type' ? 'award categories' : `${groupBy.toLowerCase()}s`} ·{' '}
          {num(totals.employees)} employees · {num(totals.awards)} awards · {usdExact(totals.amount)}
        </div>
        <Table<Node>
          size="small"
          rowKey="key"
          dataSource={tree}
          pagination={{
            current: page,
            pageSize,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, [from, to]) => `${from}-${to} of ${total}`,
            onChange: (nextPage, nextSize) => {
              // Changing the size jumps back to page 1 so the view never lands past the end.
              setPage(nextSize !== pageSize ? 1 : nextPage);
              setPageSize(nextSize);
            },
          }}
          scroll={{ x: 720 }}
          locale={{ emptyText: 'No award recipients in this slice' }}
          expandable={{
            expandedRowKeys: expanded,
            onExpandedRowsChange: (keys) => setExpanded([...keys]),
            expandRowByClick: true,
            indentSize: 22,
          }}
          columns={[
            {
              title: 'Name',
              dataIndex: 'name',
              key: 'name',
              sorter: (a: Node, b: Node) => a.name.localeCompare(b.name),
              render: (_: string, n: Node) => (
                <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.3 }}>
                  <span style={{ fontWeight: n.level === 'item' ? 400 : 600, color: '#0b0b0b' }}>
                    {n.name}
                    <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 400, color: '#898781' }}>
                      {LEVEL_LABEL[n.level]}
                    </span>
                  </span>
                  {n.sub && <span style={{ fontSize: 11.5, color: '#898781' }}>{n.sub}</span>}
                </span>
              ),
            },
            {
              title: 'Status',
              key: 'status',
              width: 120,
              sorter: (a: Node, b: Node) =>
                (STATUS_ORDER[a.status ?? ''] ?? 9) - (STATUS_ORDER[b.status ?? ''] ?? 9),
              render: (_: unknown, n: Node) => {
                if (!n.status) return null;
                const s = STATUS[n.status];
                return (
                  <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', color: '#0b0b0b' }}>
                    <span style={{ color: s?.color }}>{s?.icon}</span>
                    {n.status}
                  </span>
                );
              },
            },
            {
              title: 'Employees',
              dataIndex: 'employees',
              key: 'employees',
              sorter: (a: Node, b: Node) => a.employees - b.employees,
              align: 'right' as const,
              className: 'ins-num',
              width: 110,
              render: (v: number, n: Node) => (n.level === 'item' || n.level === 'employee' ? '' : num(v)),
            },
            {
              title: 'Awards',
              dataIndex: 'awards',
              key: 'awards',
              sorter: (a: Node, b: Node) => a.awards - b.awards,
              align: 'right' as const,
              className: 'ins-num',
              width: 90,
              render: (v: number, n: Node) => (n.level === 'item' ? '' : num(v)),
            },
            {
              title: 'Value',
              dataIndex: 'amount',
              key: 'amount',
              sorter: (a: Node, b: Node) => a.amount - b.amount,
              align: 'right' as const,
              className: 'ins-num',
              width: 120,
              render: (v: number) => usdExact(v),
            },
          ]}
        />
      </div>
    </div>
  );
};

export default AwardsDrilldown;
